import type { ChecklistItem, Stage, UseChecklistResult } from '../types';
import { useLanguage } from '../i18n/context';
import { getUi } from '../i18n/ui';
import { pickFocusItems } from '../lib/pregnancy';
import { Mascot } from './mascots';
import styles from '../App.module.css';

interface TodayFocusProps {
  stage: Stage;
  checklist: UseChecklistResult;
  snapshotLabel: string;
  onOpenSettings: () => void;
  hasProfile: boolean;
}

export function TodayFocus({
  stage,
  checklist,
  snapshotLabel,
  onOpenSettings,
  hasProfile,
}: TodayFocusProps) {
  const { locale } = useLanguage();
  const ui = getUi(locale);
  const items: ChecklistItem[] = pickFocusItems(stage, checklist.isChecked, 5);
  const done = checklist.stageCheckedCount(stage.id);
  const total = stage.checklist.length;

  return (
    <section className={styles.focusCard} aria-labelledby="today-focus-title">
      <div className={styles.focusHeader}>
        <div className={styles.focusTitleRow}>
          <Mascot id="momo" size={40} />
          <div>
            <h2 id="today-focus-title" className={styles.focusTitle}>
              {ui.focus.title}
            </h2>
            <p className={styles.focusSubtitle}>
              {hasProfile ? snapshotLabel : ui.focus.noProfile}
            </p>
          </div>
        </div>
        <button type="button" className={styles.ghostButton} onClick={onOpenSettings}>
          {hasProfile ? ui.focus.editDates : ui.focus.setDates}
        </button>
      </div>

      <div className={styles.focusMeta}>
        <span className={styles.focusStage}>{stage.title}</span>
        <span className={styles.focusCount}>
          {ui.checklist.completedCount
            .replace('{done}', String(done))
            .replace('{total}', String(total))}
        </span>
      </div>

      {items.length === 0 ? (
        <p className={styles.focusEmpty}>{ui.focus.allDone}</p>
      ) : (
        <ul className={styles.focusList}>
          {items.map((item) => (
            <li key={item.id} className={styles.focusItem}>
              <label className={styles.checklistLabel}>
                <input
                  type="checkbox"
                  className={styles.checkboxInput}
                  checked={checklist.isChecked(stage.id, item.id)}
                  onChange={() => checklist.toggle(stage.id, item.id)}
                />
                <span className={styles.checkboxVisual} aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <span className={styles.itemTextWrap}>
                  <span className={styles.itemText}>{item.text}</span>
                  <span className={styles.itemCategory}>
                    {ui.checklist.categoryLabels[item.category]}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
